import React from 'react';
import { Calendar, Clock, MapPin, Edit2, Trash2, Users, Trophy, Copy, Eye, ArrowRightLeft, ClipboardCheck, MoreHorizontal, Globe2 } from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { parseLocalDate } from '../../lib/dateUtils';
import {
  Card,
  CardContent,
  Badge,
  Button
} from '../ui';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '../ui/dropdown-menu';
import { cn, fixMojibake } from '../../lib/utils';
import { mundoDelJuego, MUNDO_RED } from '../../lib/mundoTorneo';
import { ESTATUS_STYLES, ESTATUS_LABELS, RESULTADO_STYLES, RESULTADO_LABELS } from './juegoEstatus';

const TeamBadge = ({ name, logoUrl, size = 'sm' }) => {
  const initials = (name || '?').split(' ').filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase();
  const px = size === 'sm' ? 'h-8 w-8 text-[10px]' : 'h-10 w-10 text-xs';
  if (logoUrl) {
    return <img src={logoUrl} alt={name} className={`${px} rounded-full object-contain bg-muted border`} />;
  }
  return (
    <div className={`${px} rounded-full bg-muted border flex items-center justify-center font-bold text-muted-foreground shrink-0`}>
      {initials}
    </div>
  );
};

const JuegoCard = ({
  juego,
  onEdit,
  onDuplicate,
  onDelete,
  onReassign,
  onTakeAttendance,
  onView,
  orgId,
  showActions = true,
  compact = false
}) => {
  const equipoNombre = fixMojibake(juego.equipo?.nombre || juego.equipo_nombre || 'Por definir');
  const grupoNombre = fixMojibake(juego.grupo?.nombre_del_grupo || juego.grupos?.nombre_del_grupo || 'Grupo');
  const torneoNombre = juego.torneo?.nombre ? fixMojibake(juego.torneo.nombre) : null;
  const hasCampoNumero = juego.campo_numero != null && juego.campo_numero !== '';
  const isFinished = juego.estatus === 'terminado';
  const isCancelled = juego.estatus === 'cancelado';
  const canTakeAttendance = !isFinished && !isCancelled && onTakeAttendance;
  // De cual de los dos mundos es este partido. Sin decirlo, la ausencia de los botones
  // de editar se lee como "me faltan permisos" en vez de "este partido es de otro".
  //
  // Se pregunta por el TORNEO y no por `torneo_juego_id`, que era el defecto: un partido
  // de un torneo de la red que no bajo del rol oficial se quedaba sin distintivo Y sin
  // menu, callado por partida doble. La tarjeta usa el mismo criterio que
  // `puedeEditarJuego`, asi que insignia y botones no pueden contradecirse.
  const esDeLaRed = mundoDelJuego(juego, orgId) === MUNDO_RED;
  // El NOMBRE de la organizadora no se puede decir aqui, y no es un olvido: la RLS de
  // `organizations` solo deja leer las orgs a las que perteneces (`has_org_access(id)`),
  // asi que un join a la organizadora ajena vuelve vacio. Lo sabe la RPC
  // `get_mis_torneos_con_licencia` (SECURITY DEFINER), que es de donde habria que
  // traerlo si algun dia se quiere nombrar. Mientras tanto, sin nombre pero sin mentira.

  const formatFecha = (fecha) => {
    try {
      return format(parseLocalDate(fecha) || new Date(), "d MMM", { locale: es });
    } catch { return fecha; }
  };

  const formatHora = (hora) => hora ? hora.substring(0, 5) : '--:--';

  return (
    <Card className={cn('hover:shadow-md transition-shadow', compact && 'h-full')}>
      <CardContent className={cn('pt-4 pb-3 space-y-2', compact && 'pt-3 pb-2')}>
        {/* Línea 1: Estatus + Badges */}
        <div className="flex items-center gap-1.5 flex-wrap">
          <Badge variant="outline" className={cn("text-[10px]", ESTATUS_STYLES[juego.estatus] || ESTATUS_STYLES.programado)}>
            {ESTATUS_LABELS[juego.estatus] || juego.estatus}
          </Badge>
          {esDeLaRed && (
            <Badge variant="outline" className="text-[10px] gap-1 border-blue-500/30 bg-blue-500/10 text-blue-700">
              <Globe2 className="h-3 w-3" /> MYKLUBB Torneos
            </Badge>
          )}
          {/* De que lado juega el club. Sin dato (juegos viejos) no se inventa. */}
          {juego.condicion === 'local' && <Badge variant="outline" className="text-[10px]">En casa</Badge>}
          {juego.condicion === 'visita' && <Badge variant="outline" className="text-[10px]">Visita</Badge>}
          {juego.fase && juego.fase !== 'grupal' && (
            <Badge variant="secondary" className="text-[10px]">{fixMojibake(juego.fase)}</Badge>
          )}
          {hasCampoNumero && (
            <Badge variant="outline" className="text-[10px] border-dashed">Campo {juego.campo_numero}</Badge>
          )}
          {juego.resultado && isFinished && (
            <Badge variant="outline" className={cn("text-[10px] font-medium", RESULTADO_STYLES[juego.resultado])}>
              {RESULTADO_LABELS[juego.resultado]}
            </Badge>
          )}
        </div>

        {/* Matchup: Local vs Rival */}
        <div className="flex items-center gap-2">
          <TeamBadge name={grupoNombre} />
          <div className="flex-1 min-w-0 text-center">
            {isFinished && juego.puntos_favor != null && juego.puntos_contra != null ? (
              <p className="font-bold text-base">{juego.puntos_favor} - {juego.puntos_contra}</p>
            ) : (
              <p className="text-xs text-muted-foreground font-medium">vs</p>
            )}
          </div>
          <TeamBadge name={equipoNombre} logoUrl={juego.equipo?.logo_url} />
        </div>
        <div className="flex items-center justify-between text-[11px] text-muted-foreground -mt-1 px-1">
          <span className="truncate max-w-[40%]">{grupoNombre}</span>
          <span className="truncate max-w-[40%] text-right">{equipoNombre}</span>
        </div>

        {/* Línea 3: Metadata compacta */}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
          <span className="flex items-center gap-1">
            <Calendar className="h-3 w-3" /> {formatFecha(juego.fecha)}
            {juego.jornada && <span className="text-muted-foreground/60">J{juego.jornada}</span>}
          </span>
          <span className="flex items-center gap-1">
            <Clock className="h-3 w-3" /> {formatHora(juego.hora)}
          </span>
          <span className="flex items-center gap-1">
            <Users className="h-3 w-3" /> {grupoNombre}
          </span>
          {juego.ubicacion && (
            <span className="flex items-center gap-1">
              <MapPin className="h-3 w-3" /> {fixMojibake(juego.ubicacion)}
            </span>
          )}
          {torneoNombre && (
            <span className="flex items-center gap-1">
              <Trophy className="h-3 w-3 text-amber-500" /> {torneoNombre}
            </span>
          )}
        </div>

        {/* Quien manda en este partido. La tarjeta de un torneo de la red se veia igual
            que la de uno propio salvo por un menu ausente; esto lo dice con palabras. */}
        {esDeLaRed && (
          <p className="text-[11px] text-muted-foreground">
            Lo programa la organizadora del torneo {'·'} tú tomas asistencia
          </p>
        )}

        {/* Acciones: 1 primaria + Ver + menu. Editar/Duplicar/Reasignar/Eliminar eran
            hasta 6 iconos sin etiqueta en la misma fila. */}
        {showActions && (() => {
          const hayEnMenu = onEdit || onDuplicate || onReassign || onDelete;
          return (
            <div className="flex items-center gap-2 pt-1.5 border-t">
              {canTakeAttendance && (
                <Button size="sm" className="h-8 text-xs flex-1" onClick={() => onTakeAttendance(juego)}>
                  <ClipboardCheck className="h-3.5 w-3.5 mr-1.5" /> Asistencia
                </Button>
              )}
              {onView && (
                <Button
                  size="sm"
                  variant={canTakeAttendance ? 'outline' : 'default'}
                  className="h-8 text-xs flex-1"
                  onClick={() => onView(juego)}
                >
                  <Eye className="h-3.5 w-3.5 mr-1.5" /> Ver
                </Button>
              )}
              {hayEnMenu && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0 shrink-0" title="Más acciones">
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {onEdit && (
                      <DropdownMenuItem onClick={() => onEdit(juego)}>
                        <Edit2 className="h-4 w-4" />
                        Editar juego
                      </DropdownMenuItem>
                    )}
                    {onDuplicate && (
                      <DropdownMenuItem onClick={() => onDuplicate(juego)}>
                        <Copy className="h-4 w-4" />
                        Duplicar juego
                      </DropdownMenuItem>
                    )}
                    {onReassign && (
                      <DropdownMenuItem onClick={() => onReassign(juego)}>
                        <ArrowRightLeft className="h-4 w-4" />
                        Reasignar a otro grupo
                      </DropdownMenuItem>
                    )}
                    {onDelete && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() => onDelete(juego)}
                          className="text-destructive focus:text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                          Eliminar juego
                        </DropdownMenuItem>
                      </>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          );
        })()}

        {juego.comentarios && (
          <p className="text-[11px] text-muted-foreground italic pt-1 border-t">
            "{fixMojibake(juego.comentarios)}"
          </p>
        )}
      </CardContent>
    </Card>
  );
};

export default JuegoCard;
